import { Typography, Tooltip, IconWrapper, InfoPanel, TextField } from "@king-ict/eupisi-ui/components";
import { ReactComponent as InfoIcon } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import StaticField from "../../components/StaticField";
import UploadButton from "../../components/UploadButton";
import { DocumentType } from "../../models/Document";
import { AndragogicLeader, Founder, Institution, InstitutionLocation, InstitutionType } from "../../models/Institution";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import AndragogicLeaderPanel from "./AndragogicLeaderPanel";
import AndragogicLeaderPanelV2 from "./AndragogicLeaderPanelV2";
import FounderPanel from "./FounderPanel";
import styles from "./Institution.module.scss";
import LocationPanel from "./LocationPanel";

type InstitutionProps = {
  institution: Institution,
}

export default function InstitutionPanel({ institution }: InstitutionProps): JSX.Element {
  const { t } = useTranslation();

  const { data: userSession } = useGetUserSessionQuery();

  const getFoundingActLabel = (): string => {
    if (institution.institutionTypeId === InstitutionType.HighSchool) {
      return t("institution.decisionOfEstablishmentSchool");
    }
    if (institution.institutionTypeId === InstitutionType.College) {
      return t("institution.decisionOfEstablishmentCollege");
    }
    return t("institution.foundingAct");

  };

  const getUsageAgreementLabel = (): string => {
    if (institution.institutionTypeId === InstitutionType.HighSchool) {
      return t("institution.extractFromLandRegistry");
    }
    if (institution.institutionTypeId === InstitutionType.College) {
      return t("institution.extractFromLandRegistry");
    }
    return t("institution.usageAgreement");
  };

  const getMinimumRequirementsLabel = (): string => {
    if (institution.institutionTypeId === InstitutionType.HighSchool) {
      return t("institution.decisionOfBeginningSchool");
    }
    if (institution.institutionTypeId === InstitutionType.College) {
      return t("institution.decisionOfBeginningCollege");
    }
    return t("institution.minimumRequirements");
  };

  return (
    <>
      {!institution.active &&
        <Row>
          <Col md={12}>
            <InfoPanel
              message={t("institution.deactivatedMessage", { institution: institution.name })}
              color={"warning"}
              fullWidth
            />
          </Col>
        </Row>
      }
      <Row>
        <Col md={12} className={styles.rowTitleSpacing}>
          <Typography variant="smallHeading" component="h3">
            {t("institution.basicInformation")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.dataRow}>
        <Col md={4}>
          <StaticField
            label={t("institution.institutionName")}
            value={institution.name}
            newValue={institution.nameFromOib}
          />
        </Col>
        <Col md={4}>
          <StaticField
            label={t("institution.institutionOib")}
            value={institution.oib}
          />
        </Col>
        <Col md={4}>
          <StaticField
            label={t("institution.institutionType")}
            value={institution.institutionType}
          />
        </Col>
      </Row>
      <Row className={styles.dataRow}>
        <Col md={4}>
          <StaticField
            label={t("institution.headquarterAddress")}
            value={institution.address}
            newValue={institution.addressFromOib}
          />
        </Col>
        <Col md={4}>
          <StaticField
            label={t("institution.headquarterTown")}
            value={institution.town?.name ?? ""}
            newValue={institution.townFromOib ? institution.townFromOib.name : ""}
          />
        </Col>
        <Col md={4}>
          <StaticField
            label={t("institution.headquarterMunicipality")}
            value={institution.town?.municipalityName ?? ""}
            newValue={institution.townFromOib ? institution.townFromOib.municipalityName : ""}
          />
        </Col>
      </Row>
      <Row className={styles.dataRow}>
        <Col md={4}>
          <StaticField
            label={t("institution.headquarterCounty")}
            value={institution.town?.countyName ?? ""}
            newValue={institution.townFromOib ? institution.townFromOib.countyName : ""}
          />
        </Col>
        <Col md={4}>
          <StaticField
            label={t("institution.email")}
            value={institution.email}
          />
        </Col>
        <Col md={4}>
          <StaticField
            label={t("institution.phone")}
            value={institution.phoneNumber}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} className={styles.rowTitleSpacing}>
          <Typography variant="smallHeading" component="h3">
            {t("institution.foundersInformation")}
          </Typography>
        </Col>
      </Row>

      {institution.founders.map((founder: Founder, founderIndex: number) => (
        <FounderPanel key={founder.id.toString()} founderData={founder} panelIndex={founderIndex} readOnly={true} />
      ))}

      <Row>
        <Col md={12} className={styles.rowTitleSpacing}>
          <Typography variant="smallHeading" component="h3">
            {t("institution.institutionDocuments")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.dataRow}>
        <Col md={4} className={styles.hideUploadButton}>
          <UploadButton
            label={getFoundingActLabel()}
            name={nameof<typeof institution>("documentFoundingAct")}
            value={institution.documentFoundingAct}
            disabled={true}
            documentType={DocumentType.DocumentFoundingAct}
          />
        </Col>
        <Col md={4} className={styles.hideUploadButton}>
          <UploadButton
            label={getUsageAgreementLabel()}
            name={nameof<typeof institution>("documentUsageAgreement")}
            value={institution.documentUsageAgreement}
            disabled={true}
            documentType={DocumentType.UsageAgreement}
          />
        </Col>
        <Col md={4} className={styles.hideUploadButton}>
          <UploadButton
            label={getMinimumRequirementsLabel()}
            name={nameof<typeof institution>("documentMinimumRequirement")}
            value={institution.documentMinimumRequirement}
            disabled={true}
            documentType={DocumentType.MinimumRequirementSolution}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} className={styles.rowTitleSpacing}>
          <Typography variant="smallHeading" component="h3">
            {t("institution.locationsOutsideHeadquarter")}
            <div className={styles.infoTooltipPanel}>
              <Tooltip
                overlay={<div className={styles.infoTooltip}>{t("institution.locationUserInfo")}</div>}
                placement="right"
              >
                <IconWrapper color="primary">
                  <InfoIcon />
                </IconWrapper>
              </Tooltip>
            </div>
          </Typography>
        </Col>
      </Row>

      {institution.locations.map((location: InstitutionLocation, locationIndex: number) => (
        <LocationPanel
          name={`${nameof<typeof institution>("locations")}`}
          key={locationIndex.toString()}
          location={location}
          locations={institution.locations}
          panelIndex={locationIndex}
          readOnly={true}
          institutionTypeId={institution.institutionTypeId}
          roleId={userSession?.roleId}
          getErrorForPath={() => { return undefined; }}
          onChange={() => { }}
        />
      ))}

      {institution.andragogicLeaders && institution.andragogicLeaders.length > 0 && (
        <>
          <Row>
            <Col md={12} className={styles.rowTitleSpacing}>
              <Typography variant="smallHeading" component="h3">
                {t("institution.andragogicLeader.title")}
              </Typography>
            </Col>
          </Row>
          {/* {institution.andragogicLeaders.map((andragogicLeader: AndragogicLeader, andragogicLeaderIndex: number) => (
        <AndragogicLeaderPanel 
          key={`${andragogicLeader?.id}andragogicLeader${andragogicLeaderIndex}`} 
          andragogicLeaderData={andragogicLeader} 
          locations={institution.locations} 
          panelIndex={andragogicLeaderIndex} 
          readOnly={true} 
          onChange={() => {}}
        />
      ))}      */}
          <AndragogicLeaderPanelV2
            name={`${nameof<typeof institution>("andragogicLeaders")}`}
            andragogicLeaders={institution.andragogicLeaders}
            locations={institution.locations}
            institution={institution}
            readOnly={true}
            onChange={() => { }}
            getErrorForPath={() => { return undefined; }}
            doValidations={() => { return undefined; }}
          />

        </>

      )}

    </>
  );
}
